import { Component, computed, inject, signal } from '@angular/core';
import { Helpers } from '../../../../../utilities/helpers';
import { ShimmerEffect } from '../../../../../shared/shimmer-effect/shimmer-effect';
import { ApiEndpoints } from '../../../../../core/api-endpoints';
import { IStudentEnrollments } from '../../../../../interfaces/student';
import { StudentService } from '../../../../../services/student';
import { ActivatedRoute, Router } from '@angular/router';
import { Session } from '../../../../../services/session';
import { FlashService } from '../../../../../services/flash';
import { Pagination } from '../../../../../shared/pagination/pagination';
import { FormsModule, NgForm } from '@angular/forms';
import { LoadingIndicator } from '../../../../../shared/loading-indicator/loading-indicator';
import { ClassService } from '../../../../../services/class';
import { AcademicTermService } from '../../../../../services/academic-term';
import { AssessmentService } from '../../../../../services/assessment';
import { GradeSystemsService } from '../../../../../services/grade-systems';
import { IGradeSystem } from '../../../../../interfaces/grade-system';
import { IClass } from '../../../../../interfaces/class';
import {
  IAssessmentComponentPayload,
  IAssessmentConfigPayload,
  IRecordStudentScoresPayload,
  IStudentScoreEntry,
  IStudentScoreSummary,
} from '../../../../../interfaces/assessment';
import { concat, Observable } from 'rxjs';
import { DecimalPipe } from '@angular/common';
import { SubjectService } from '../../../../../services/subject';

interface ClassScoreBreakdownRow {
  id?: string;
  component_name: string;
  maximum_score: number | null;
}

interface StudentScoreState {
  componentScores: Record<string, number | null>;
  examScores: Record<string, number | null>;
  examMaximumScores: Record<string, number>;
  componentScoreIds: Record<string, string>;
  examScoreIds: Record<string, string>;
  componentLocks: Record<string, boolean>;
  examLocks: Record<string, boolean>;
  summaries: Record<string, IStudentScoreSummary>;
}

@Component({
  selector: 'app-class-assessment',
  imports: [ShimmerEffect, Pagination, FormsModule, LoadingIndicator, DecimalPipe],
  templateUrl: './class-assessment.html',
  styleUrl: './class-assessment.css',
})
export class ClassAssessment {
  constructor(
    private studentService: StudentService,
    private route: ActivatedRoute,
    private router: Router,
    private session: Session,
    private classService: ClassService,
    private academicTermService: AcademicTermService,
    private assessmentService: AssessmentService,
    private gradeSystemsService: GradeSystemsService,
    protected subjectService: SubjectService,
  ) {
    this.route.queryParamMap.subscribe((params) => {
      this.classId = params.get('id')!;
      this.levelSubjectId = params.get('level_subject_id') ?? '';
    });

    if (!this.classId) {
      void this.router.navigate(['/dashboard/class']);
    }

    const token = this.session.getItem('token');

    this.loggedUserRole = this.session.decodeToken(token)?.role;
    this.loggedUserId = this.session.decodeToken(token)?.sub;

    if (this.loggedUserRole === 'FINANCE_ADMIN') {
      void this.router.navigate(['/dashboard/class']);
    }

    this.getClassStudents();
    this.getAssessmentContext();
    this.getTeacherClassSubjects();
  }
  protected isLoading = signal<boolean>(false);
  protected classId!: string;
  protected levelSubjectId = '';
  protected classData = signal<IClass | null>(null);
  protected assessmentConfig = signal<IAssessmentConfigPayload | null>(null);
  protected gradingScales = signal<IGradeSystem['payload']['scales']>([]);
  protected assessmentConfigId = '';
  protected classStudents = signal<IStudentEnrollments | null>(null);
  protected currentFilterType = '';
  protected currentFilterValue = '';
  protected isFiltering = signal<boolean>(false);
  protected pagination = signal({ page: 0, size: 10 });
  protected loggedUserRole?: string;
  protected loggedUserId?: string;
  protected readonly flashService = inject(FlashService);
  protected readonly Helpers = Helpers;
  protected setClassBreakdown = signal<boolean>(false);
  protected isSubmitting = signal<boolean>(false);
  protected isSavingBreakdown = signal<boolean>(false);
  protected classScoreComponents = signal<IAssessmentComponentPayload[]>([]);
  protected breakdownRows: ClassScoreBreakdownRow[] = [];
  protected componentScores = signal<Record<string, number | null>>({});
  protected examScores = signal<Record<string, number | null>>({});
  protected examMaximumScores = signal<Record<string, number>>({});
  protected scoreSummaries = signal<Record<string, IStudentScoreSummary>>({});
  protected isLoadingScores = signal<boolean>(false);
  protected isLockingScores = signal<boolean>(false);
  protected showUnlockPopup = signal<boolean>(false);
  protected unlockReason = '';
  protected componentScoreIds = signal<Record<string, string>>({});
  protected examScoreIds = signal<Record<string, string>>({});
  protected componentLocks = signal<Record<string, boolean>>({});
  protected examLocks = signal<Record<string, boolean>>({});
  protected readonly scoresLocked = computed(() => this.hasLockedScores);
  protected studentSearch: string = '';
  protected readonly examMaximumScore = 100;
  protected hideScoreSheet = signal<boolean>(false);
  protected readonly isAssignedTeacher = signal<boolean>(false);

  protected get isAdmin(): boolean {
    return ['SUPER_ADMIN', 'SCHOOL_ADMIN'].includes(this.loggedUserRole ?? '');
  }

  protected get canUnlockScores(): boolean {
    return this.isAdmin;
  }

  protected get canLockScores(): boolean {
    return this.isAdmin || this.isAssignedTeacher();
  }

  protected get scoresFinalized(): boolean {
    return Boolean(this.assessmentConfig()?.is_finalized);
  }

  protected getTeacherClassSubjects(): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 100,
      class_id: this.classId,
      level_subject_id: this.levelSubjectId,
    };

    this.subjectService
      .getTeacherClassSubjects(`${ApiEndpoints.url}/teacher-class-subject`, params)
      .subscribe({
        next: (res) => {
          const isAssigned = (res.payload ?? []).some(
            (assignment) => assignment.user_id === this.loggedUserId,
          );

          this.isAssignedTeacher.set(isAssigned);

          if (this.loggedUserRole === 'TEACHER' && !isAssigned) {
            void this.router.navigate(['/dashboard/class']);
          }
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not load subjects for teachers. Refresh the page',
          );
        },
      });
  }

  protected get hasLockedScores(): boolean {
    return (
      Object.values(this.componentLocks()).some(Boolean) ||
      Object.values(this.examLocks()).some(Boolean)
    );
  }

  protected get hasSavedScores(): boolean {
    return (
      Object.keys(this.componentScoreIds()).length > 0 ||
      Object.keys(this.examScoreIds()).length > 0
    );
  }

  protected get totalStudents(): number {
    return this.classStudents()?.pagination?.totalElements ?? 0;
  }

  protected get completedEntries(): number {
    const completed = Object.values(this.scoreSummaries()).filter(
      (summary) => summary.is_complete,
    ).length;

    return Math.min(completed, this.totalStudents);
  }

  protected get remainingEntries(): number {
    return Math.max(this.totalStudents - this.completedEntries, 0);
  }

  protected get entryProgress(): number {
    if (!this.totalStudents) {
      return 0;
    }

    const percentage = (this.completedEntries / this.totalStudents) * 100;

    return Math.min(Math.max(Math.round(percentage), 0), 100);
  }

  protected get examWeight(): number {
    return Number(this.assessmentConfig()?.examination_score);
  }

  protected get classScoreMaximum(): number {
    return Number(this.assessmentConfig()?.class_score);
  }

  protected get breakdownTotal(): number {
    const total = this.breakdownRows.reduce(
      (sum, row) => sum + (Number(row.maximum_score) || 0),
      0,
    );

    return Math.round(total * 100) / 100;
  }

  protected openClassScoreBreakdown(): void {
    if (this.isLoading()) {
      return;
    }

    if (!this.assessmentConfigId) {
      this.flashService.failure(
        'The assessment configuration is not loaded for this class and subject.',
      );
      return;
    }

    const components = this.classScoreComponents();

    this.breakdownRows = components.length
      ? components.map((component) => ({
          id: component.id,
          component_name: component.component_name,
          maximum_score: Number(component.maximum_score),
        }))
      : [{ component_name: '', maximum_score: null }];

    this.setClassBreakdown.set(true);
  }

  protected addBreakdownRow(): void {
    this.breakdownRows = [...this.breakdownRows, { component_name: '', maximum_score: null }];
  }

  protected removeBreakdownRow(index: number): void {
    this.breakdownRows = this.breakdownRows.filter((_, i) => i !== index);
  }

  protected scoreKey(enrollmentId: string, componentId: string): string {
    return `${enrollmentId}:${componentId}`;
  }

  protected setComponentScore(
    enrollmentId: string,
    componentId: string,
    score: number | null,
  ): void {
    const key = this.scoreKey(enrollmentId, componentId);

    this.componentScores.update((scores) => ({
      ...scores,
      [key]: score,
    }));
  }

  protected setExamScore(enrollmentId: string, score: number | null): void {
    this.examScores.update((scores) => ({
      ...scores,
      [enrollmentId]: score,
    }));
  }

  protected examMaximumFor(enrollmentId: string): number {
    return this.examMaximumScores()[enrollmentId] ?? this.examMaximumScore;
  }

  protected classScoreFor(enrollmentId: string): string {
    return this.formatSummaryScore(this.scoreSummaries()[enrollmentId]?.weighted_class_score);
  }

  protected totalScoreFor(enrollmentId: string): string {
    return this.formatSummaryScore(this.scoreSummaries()[enrollmentId]?.final_score);
  }

  protected gradeFor(enrollmentId: string): string {
    if (!this.scoresLocked()) {
      return '—';
    }

    const finalScore = this.scoreSummaries()[enrollmentId]?.final_score;

    if (finalScore === null || finalScore === undefined) {
      return '—';
    }

    const numericScore = Number(finalScore);

    if (!Number.isFinite(numericScore)) {
      return '—';
    }

    const scale = this.gradingScales().find(
      (item) =>
        numericScore >= Number(item.minimum_score) && numericScore <= Number(item.maximum_score),
    );

    return scale?.grade ?? '—';
  }

  private formatSummaryScore(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return '—';
    }

    const numeric = Number(value);
    return Number.isFinite(numeric) ? String(Number(numeric.toFixed(2))) : '—';
  }

  protected loadStudentsPage(page: number): void {
    this.pagination.update((p) => ({
      ...p,
      page,
    }));

    this.getClassStudents();
  }

  protected getClassStudents(
    filterType = this.currentFilterType,
    value = this.currentFilterValue,
  ): void {
    this.isFiltering.set(true);

    let { page, size } = this.pagination();

    const params: Record<string, string | number> = {
      page,
      size,
      status: 'ACTIVE',
      class_id: this.classId,
    };

    if (filterType && value) {
      params[filterType] = value;
    }

    this.studentService
      .getStudentEnrollments(`${ApiEndpoints.url}/student-enrollment`, params)
      .subscribe({
        next: (data) => {
          this.classStudents.set(data);
          this.isFiltering.set(false);
        },
        error: (err) => {
          this.isFiltering.set(false);
          this.flashService.failure(
            err?.error?.message ?? 'Could not load students. Refresh the page',
          );
        },
      });
  }

  private getAssessmentContext(): void {
    if (!this.levelSubjectId) {
      this.flashService.failure(
        'The assessment subject was not provided. Open this page from the class subject list.',
      );
      return;
    }

    this.isLoading.set(true);

    this.classService.getClassById(`${ApiEndpoints.url}/class/${this.classId}`).subscribe({
      next: (res) => {
        this.classData.set(res);
        this.getCurrentTerm();
      },
      error: (err) => {
        this.isLoading.set(false);
        this.flashService.failure(err?.error?.message ?? 'Could not load class. Refresh the page.');
      },
    });
  }

  private getCurrentTerm(): void {
    const params: Record<string, string | number | boolean> = {
      page: 0,
      size: 1,
      current_term: true,
    };

    this.academicTermService
      .getAcademicTerms(`${ApiEndpoints.url}/academic-term`, params)
      .subscribe({
        next: (res) => {
          const term = res.payload?.[0];

          if (!term) {
            this.isLoading.set(false);
            this.flashService.failure('No current academic term is configured.');
            return;
          }

          this.getAssessmentConfig(term.id);
        },
        error: (err) => {
          this.isLoading.set(false);
          this.flashService.failure(
            err?.error?.message ?? 'Could not load the current academic term.',
          );
        },
      });
  }

  private getAssessmentConfig(termId: string): void {
    const academicYearId = this.classData()?.payload?.academic_year_id;

    if (!academicYearId) {
      this.isLoading.set(false);
      this.flashService.failure('Could not determine the academic year of this class.');
      return;
    }

    const params: Record<string, string | number> = {
      page: 0,
      size: 1,
      academic_year_id: academicYearId,
      term_id: termId,
      level_subject_id: this.levelSubjectId,
      // status: 'ACTIVE'
    };

    this.assessmentService
      .getAssessmentConfigs(`${ApiEndpoints.url}/assessment-config`, params)
      .subscribe({
        next: (res) => {
          const config = res.payload?.[0];

          if (!config) {
            this.hideScoreSheet.set(true);
            this.isLoading.set(false);
            this.flashService.failure('No active assessment configuration found for this class.');
            return;
          }

          this.assessmentConfig.set(config);
          this.assessmentConfigId = config.id;
          this.getGradingScales(config.grading_system_id);
          this.getAssessmentComponents(config.id);
          this.getStudentScores(config.id);
        },
        error: (err) => {
          this.isLoading.set(false);
          this.flashService.failure(
            err?.error?.message ?? 'Could not load the assessment configuration.',
          );
        },
      });
  }

  private getGradingScales(gradingSystemId: string | null): void {
    if (!gradingSystemId) {
      this.gradingScales.set([]);
      return;
    }

    this.gradeSystemsService
      .getGradeSystem(`${ApiEndpoints.url}/grading-system/${gradingSystemId}`)
      .subscribe({
        next: (res) => {
          this.gradingScales.set(res.payload?.scales ?? []);
        },
        error: (err) => {
          this.gradingScales.set([]);
          this.flashService.failure(
            err?.error?.message ?? 'Could not load the grading scales for this assessment.',
          );
        },
      });
  }

  private getAssessmentComponents(assessmentConfigId: string): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 50,
      assessment_config_id: assessmentConfigId,
      status: 'ACTIVE',
    };

    this.assessmentService
      .getAssessmentComponents(`${ApiEndpoints.url}/assessment-component`, params)
      .subscribe({
        next: (res) => {
          this.classScoreComponents.set(res.payload ?? []);
          this.isLoading.set(false);
        },
        error: (err) => {
          this.isLoading.set(false);
          this.flashService.failure(
            err?.error?.message ?? 'Could not load the assessment components.',
          );
        },
      });
  }

  private getStudentScores(
    assessmentConfigId: string,
    page = 1,
    state: StudentScoreState = {
      componentScores: {},
      examScores: {},
      examMaximumScores: {},
      componentScoreIds: {},
      examScoreIds: {},
      componentLocks: {},
      examLocks: {},
      summaries: {},
    },
  ): void {
    this.isLoadingScores.set(true);

    const params = {
      page,
      size: 500,
      assessment_config_id: assessmentConfigId,
      class_id: this.classId,
    };

    this.assessmentService
      .getStudentScores(`${ApiEndpoints.url}/student-component-score`, params)
      .subscribe({
        next: (res) => {
          for (const score of res.payload?.component_scores ?? []) {
            const key = this.scoreKey(score.student_enrollment_id, score.assessment_component_id);

            state.componentScores[key] = Number(score.score);
            state.componentScoreIds[key] = score.id;
            state.componentLocks[key] = Boolean(score.is_locked);
          }

          for (const summary of res.payload?.summaries ?? []) {
            state.summaries[summary.student_enrollment_id] = summary;
          }

          if (page === 1) {
            for (const exam of res.payload?.exam_scores ?? []) {
              state.examScores[exam.student_enrollment_id] = Number(exam.raw_score);
              state.examScoreIds[exam.student_enrollment_id] = exam.id;
              state.examLocks[exam.student_enrollment_id] = Boolean(exam.is_locked);

              if (exam.raw_maximum_score !== null && exam.raw_maximum_score !== undefined) {
                state.examMaximumScores[exam.student_enrollment_id] = Number(
                  exam.raw_maximum_score,
                );
              }
            }
          }

          if (res.pagination?.hasNext) {
            this.getStudentScores(assessmentConfigId, page + 1, state);
            return;
          }

          this.componentScores.set(state.componentScores);
          this.examScores.set(state.examScores);
          this.examMaximumScores.set(state.examMaximumScores);
          this.scoreSummaries.set(state.summaries);
          this.componentScoreIds.set(state.componentScoreIds);
          this.examScoreIds.set(state.examScoreIds);
          this.componentLocks.set(state.componentLocks);
          this.examLocks.set(state.examLocks);
          this.isLoadingScores.set(false);
        },
        error: (err) => {
          this.isLoadingScores.set(false);
          this.flashService.failure(err?.error?.message ?? 'Could not load student scores.');
        },
      });
  }

  protected lockScores(): void {
    if (!this.assessmentConfigId) {
      this.flashService.failure('The assessment configuration is not loaded.');
      return;
    }

    if (!this.canLockScores) {
      this.flashService.failure('You are not allowed to lock scores for this class subject.');
      return;
    }

    if (this.scoresFinalized) {
      this.flashService.failure('Finalized scores cannot be changed.');
      return;
    }

    if (this.isLoadingScores() || this.isLockingScores() || this.scoresLocked()) {
      return;
    }

    if (!this.hasSavedScores) {
      this.flashService.failure('Save at least one score before locking.');
      return;
    }

    this.isLockingScores.set(true);

    this.assessmentService
      .bulkLockStudentScores(`${ApiEndpoints.url}/student-score/bulk-lock`, {
        assessment_config_id: this.assessmentConfigId,
        class_id: this.classId,
      })
      .subscribe({
        next: () => {
          this.isLockingScores.set(false);
          this.flashService.success('Student scores locked successfully.');
          this.getStudentScores(this.assessmentConfigId);
        },
        error: (err) => {
          this.isLockingScores.set(false);
          this.flashService.failure(
            err?.error?.message ?? 'Could not lock all student scores. Try again.',
          );
        },
      });
  }

  protected openUnlockPopup(): void {
    if (
      !this.canUnlockScores ||
      this.isLockingScores() ||
      !this.hasSavedScores ||
      this.scoresFinalized
    ) {
      return;
    }

    this.unlockReason = '';
    this.showUnlockPopup.set(true);
  }

  protected cancelUnlock(): void {
    if (this.isLockingScores()) {
      return;
    }

    this.unlockReason = '';
    this.showUnlockPopup.set(false);
  }

  protected unlockScores(): void {
    if (!this.canUnlockScores || this.isLockingScores()) {
      return;
    }

    if (this.scoresFinalized) {
      this.flashService.failure('Finalized scores cannot be unlocked.');
      return;
    }

    if (!this.assessmentConfigId) {
      this.flashService.failure('The assessment configuration is not loaded.');
      return;
    }

    const reason = this.unlockReason.trim();

    if (!reason) {
      this.flashService.failure('Provide a reason for unlocking these scores.');
      return;
    }

    this.isLockingScores.set(true);

    this.assessmentService
      .bulkUnlockStudentScores(`${ApiEndpoints.url}/student-score/bulk-unlock`, {
        assessment_config_id: this.assessmentConfigId,
        class_id: this.classId,
        reason,
      })
      .subscribe({
        next: () => {
          this.isLockingScores.set(false);
          this.showUnlockPopup.set(false);
          this.unlockReason = '';
          this.flashService.success('Student scores unlocked successfully.');
          this.getStudentScores(this.assessmentConfigId);
        },
        error: (err) => {
          this.isLockingScores.set(false);
          this.flashService.failure(
            err?.error?.message ?? 'Could not unlock all student scores. Try again.',
          );
        },
      });
  }

  protected saveScores(form: NgForm): void {
    if (!this.assessmentConfigId) {
      this.flashService.failure('The assessment configuration is not loaded.');
      return;
    }

    if (this.scoresLocked()) {
      this.flashService.failure('Unlock the scores before changing them.');
      return;
    }

    if (this.scoresFinalized) {
      this.flashService.failure('Finalized scores cannot be changed.');
      return;
    }

    const components = this.classScoreComponents();
    const students = this.classStudents()?.payload ?? [];
    const formValues = form.value ?? {};
    const scores: IStudentScoreEntry[] = [];
    let hasInvalidScore = false;

    students.forEach((student) => {
      const componentScores: IStudentScoreEntry['components'] = [];

      components.forEach((component) => {
        const value = formValues[`component_${student.id}_${component.id}`];

        if (value === null || value === undefined || value === '') {
          return;
        }

        const score = Number(value);
        const maximum = Number(component.maximum_score);

        if (!Number.isFinite(score) || score < 0 || score > maximum) {
          hasInvalidScore = true;
          return;
        }

        componentScores.push({
          assessment_component_id: component.id,
          score,
        });
      });

      const examValue = formValues[`exam_${student.id}`];
      let exam: IStudentScoreEntry['exam'];

      if (examValue !== null && examValue !== undefined && examValue !== '') {
        const rawScore = Number(examValue);
        const rawMaximumScore = this.examMaximumFor(student.id);

        if (!Number.isFinite(rawScore) || rawScore < 0 || rawScore > rawMaximumScore) {
          hasInvalidScore = true;
        } else {
          exam = {
            raw_score: rawScore,
            raw_maximum_score: rawMaximumScore,
          };
        }
      }

      if (componentScores.length || exam) {
        scores.push({
          student_enrollment_id: student.id,
          components: componentScores,
          ...(exam ? { exam } : {}),
        });
      }
    });

    if (hasInvalidScore) {
      this.flashService.failure('Scores must be within the allowed maximum values.');
      return;
    }

    if (!scores.length) {
      this.flashService.failure('Enter at least one score before saving.');
      return;
    }

    const payload: IRecordStudentScoresPayload = {
      assessment_config_id: this.assessmentConfigId,
      class_id: this.classId,
      scores,
    };

    this.isSubmitting.set(true);

    this.assessmentService
      .createStudentScores(`${ApiEndpoints.url}/student-component-score`, payload)
      .subscribe({
        next: () => {
          this.isSubmitting.set(false);
          this.flashService.success('Student scores recorded successfully.');
          this.getStudentScores(this.assessmentConfigId);
        },
        error: (err) => {
          this.isSubmitting.set(false);
          this.flashService.failure(
            err?.error?.message ?? 'Could not record student scores. Try again.',
          );
        },
      });
  }

  protected setClassScoreComponents(form: NgForm): void {
    form.control.markAllAsTouched();

    if (!this.assessmentConfigId) {
      this.flashService.failure(
        'The assessment configuration is not loaded for this class and subject.',
      );
      return;
    }

    const rows = this.breakdownRows
      .map((row) => ({
        ...row,
        component_name: row.component_name.trim(),
        maximum_score: row.maximum_score === null ? null : Number(row.maximum_score),
      }))
      .filter((row) => row.component_name || (row.maximum_score ?? 0) > 0);

    if (!rows.length) {
      this.flashService.failure('Add at least one class score component.');
      return;
    }

    const names = new Set<string>();

    for (const row of rows) {
      if (!row.component_name) {
        this.flashService.failure('Give every component a name.');
        return;
      }

      const normalizedName = row.component_name.toLowerCase();

      if (names.has(normalizedName)) {
        this.flashService.failure(`"${row.component_name}" is used more than once.`);
        return;
      }

      names.add(normalizedName);

      if (
        row.maximum_score === null ||
        !Number.isFinite(row.maximum_score) ||
        row.maximum_score <= 0
      ) {
        this.flashService.failure(`Give "${row.component_name}" a maximum score greater than 0.`);
        return;
      }
    }

    const existingComponents = new Map(
      this.classScoreComponents().map((component) => [component.id, component]),
    );
    const keptIds = new Set(rows.map((row) => row.id).filter((id): id is string => Boolean(id)));

    const newRows = rows.filter((row) => !row.id);

    if (newRows.length > 50) {
      this.flashService.failure('A maximum of 50 assessment components can be added at once.');
      return;
    }

    const updatedRows = rows.filter((row) => {
      if (!row.id) {
        return false;
      }

      const existing = existingComponents.get(row.id);

      return (
        existing &&
        (existing.component_name !== row.component_name ||
          Number(existing.maximum_score) !== Number(row.maximum_score))
      );
    });
    const removedIds = [...existingComponents.keys()].filter((id) => !keptIds.has(id));

    const requests: Observable<unknown>[] = [];

    for (const id of removedIds) {
      requests.push(
        this.assessmentService.deleteAssessmentComponent(
          `${ApiEndpoints.url}/assessment-component/${id}`,
        ),
      );
    }

    for (const row of updatedRows) {
      requests.push(
        this.assessmentService.updateAssessmentComponent(
          `${ApiEndpoints.url}/assessment-component/${row.id}`,
          {
            component_name: row.component_name,
            maximum_score: Number(row.maximum_score),
          },
        ),
      );
    }

    if (newRows.length) {
      requests.push(
        this.assessmentService.createAssessmentComponents(
          `${ApiEndpoints.url}/assessment-component`,
          {
            assessment_config_id: this.assessmentConfigId,
            components: newRows.map((row) => ({
              component_name: row.component_name,
              maximum_score: Number(row.maximum_score),
              status: 'ACTIVE',
            })),
          },
        ),
      );
    }

    if (!requests.length) {
      this.setClassBreakdown.set(false);
      return;
    }

    this.isSavingBreakdown.set(true);

    let completedRequests = 0;

    concat(...requests).subscribe({
      next: () => {
        completedRequests += 1;
      },
      error: (err) => {
        this.isSavingBreakdown.set(false);

        if (completedRequests > 0) {
          this.setClassBreakdown.set(false);
          this.breakdownRows = [];
          this.getAssessmentComponents(this.assessmentConfigId);
        }

        this.flashService.failure(
          err?.error?.message ?? 'Could not save the class score breakdown.',
        );
      },
      complete: () => {
        this.isSavingBreakdown.set(false);
        this.setClassBreakdown.set(false);
        this.breakdownRows = [];
        this.flashService.success('Class score breakdown saved successfully.');
        this.getAssessmentComponents(this.assessmentConfigId);
      },
    });
  }
}
